/**
 * `Markdown` — renderer markdown minimale, senza dipendenze esterne e senza
 * `dangerouslySetInnerHTML` (nessun HTML grezzo dal sorgente: tutto è tradotto
 * in elementi React, quindi il testo è sempre escapato dal runtime di React).
 *
 * Sottoinsieme supportato, sufficiente per un changelog:
 *  - heading `#`..`######`;
 *  - liste non ordinate (`-`, `*`, `+`) e ordinate (`1.`);
 *  - regola orizzontale (`---`);
 *  - paragrafi;
 *  - inline: grassetto `**testo**`, corsivo `*testo*`, codice `` `code` ``,
 *    link `[testo](url)`.
 *
 * Non supportato di proposito (non serve al changelog): blocchi di codice
 * multilinea, tabelle, immagini, HTML inline, citazioni.
 */
import type { ReactNode } from 'react';

interface MarkdownProps {
  /** Testo markdown grezzo. */
  source: string;
  /** Classe applicata al contenitore. */
  className?: string;
}

/** Rende il testo inline di una riga in nodi React (bold, italic, code, link). */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  // Pattern in ordine di precedenza. `code` prima di bold/italic per non
  // interpretare markup dentro backtick.
  const pattern =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/g;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;

    if (token.startsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else {
      // Link: [label](url)
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        const [, label, url] = linkMatch;
        const external = /^https?:\/\//.test(url);
        nodes.push(
          <a
            key={key}
            href={url}
            {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
          >
            {label}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

export function Markdown({ source, className }: MarkdownProps) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  // Buffer per raggruppare le righe di uno stesso blocco (paragrafo o lista).
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let listOrdered = false;
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ');
    blocks.push(<p key={`p-${key++}`}>{renderInline(text, `p-${key}`)}</p>);
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const items = listItems.map((item, idx) => (
      <li key={`li-${key}-${idx}`}>{renderInline(item, `li-${key}-${idx}`)}</li>
    ));
    blocks.push(
      listOrdered ? (
        <ol key={`ol-${key++}`}>{items}</ol>
      ) : (
        <ul key={`ul-${key++}`}>{items}</ul>
      ),
    );
    listItems = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    // Riga vuota: chiude i blocchi correnti.
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    // Regola orizzontale.
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushParagraph();
      flushList();
      blocks.push(<hr key={`hr-${key++}`} />);
      continue;
    }

    // Heading.
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      const content = renderInline(heading[2], `h-${key}`);
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(<Tag key={`h-${key++}`}>{content}</Tag>);
      continue;
    }

    // Voce di lista ordinata.
    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (ordered) {
      flushParagraph();
      if (!listOrdered) flushList();
      listOrdered = true;
      listItems.push(ordered[1]);
      continue;
    }

    // Voce di lista non ordinata.
    const unordered = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (unordered) {
      flushParagraph();
      if (listOrdered) flushList();
      listOrdered = false;
      listItems.push(unordered[1]);
      continue;
    }

    // Riga di testo normale: accumula nel paragrafo (chiudendo eventuali liste).
    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();

  return <div className={className}>{blocks}</div>;
}
